import { __read, __assign } from 'tslib';
import * as React from 'react';

var Move = {
    Backward: -1,
    Neutral: 0,
    Forward: 1
};
var moveMap = {
    ArrowRight: {
        x: Move.Forward,
        y: Move.Neutral
    },
    ArrowLeft: {
        x: Move.Backward,
        y: Move.Neutral
    },
    ArrowUp: {
        x: Move.Neutral,
        y: Move.Backward
    },
    ArrowDown: {
        x: Move.Neutral,
        y: Move.Forward
    }
};
var initialState = {
    focusedDay: null,
    isLimitReached: false,
    pendingMoveKey: null,
    prevFocusedDayOutOfBounds: false
};
var firstWeekIndexInMonth = 0;
var firstMonthIndex = 0;
var firstDayIndexInWeek = 0;
var getNewFocusDayPosition = function (focusedDay, pendingMoveKey, monthsData) {
    switch (pendingMoveKey) {
        case 'ArrowRight': {
            return {
                weekIndex: firstWeekIndexInMonth,
                monthIndex: firstMonthIndex,
                dayIndex: firstDayIndexInWeek
            };
        }
        case 'ArrowLeft': {
            var lastMonthIndex = monthsData.length - 1;
            var lastWeekIndex = monthsData[lastMonthIndex].weeks.length - 1;
            var lastDayInWeek = monthsData[lastMonthIndex].weeks[lastWeekIndex].length - 1;
            return {
                weekIndex: lastWeekIndex,
                monthIndex: lastMonthIndex,
                dayIndex: lastDayInWeek
            };
        }
        case 'ArrowDown': {
            return {
                weekIndex: firstDayIndexInWeek,
                monthIndex: firstMonthIndex,
                dayIndex: focusedDay.position.dayIndex
            };
        }
        case 'ArrowUp': {
            var lastMonthIndex = monthsData.length - 1;
            return {
                weekIndex: monthsData[lastMonthIndex].weeks.length - 1,
                dayIndex: focusedDay.position.dayIndex,
                monthIndex: lastMonthIndex
            };
        }
        default: {
            return {
                weekIndex: firstDayIndexInWeek,
                monthIndex: firstMonthIndex,
                dayIndex: firstDayIndexInWeek
            };
        }
    }
};
var getNormalizedValue = function (value, maxValue) { return ({
    value: (maxValue + value) % maxValue,
    overflowCarryover: Math.floor(value / maxValue)
}); };
var movePositionInAxis = function (value, maxIndexPosition) {
    var _a = getNormalizedValue(value, maxIndexPosition), nextValue = _a.value, overflowCarryover = _a.overflowCarryover;
    return {
        value: nextValue,
        overflowCarryover: overflowCarryover
    };
};
var moveDayCursorHorizontally = function (currentPosition, moveOperation, monthsData) {
    var maxDaysInWeek = monthsData[currentPosition.monthIndex].weeks[currentPosition.weekIndex].length;
    return movePositionInAxis(currentPosition.dayIndex + moveOperation.x, maxDaysInWeek);
};
var moveDayCursorVertically = function (currentPosition, moveOperation, yOverflow, monthsData) {
    var maxWeeksInMonth = monthsData[currentPosition.monthIndex].weeks.length;
    return movePositionInAxis(currentPosition.weekIndex + moveOperation.y + yOverflow, maxWeeksInMonth);
};
var moveCursorPosition = function (dayPosition, moveOperation, monthsData) {
    var _a = moveDayCursorHorizontally(dayPosition, moveOperation, monthsData), xPosition = _a.value, yOverflow = _a.overflowCarryover;
    var _b = moveDayCursorVertically(dayPosition, moveOperation, yOverflow, monthsData), yPosition = _b.value, monthIndexOverflow = _b.overflowCarryover;
    var monthIndex = dayPosition.monthIndex;
    var newMonthIndex = monthIndex + monthIndexOverflow;
    var numberOfAvailableMonths = monthsData.length;
    // Return null position if the move overflows the existing months
    if (newMonthIndex < 0 || newMonthIndex >= numberOfAvailableMonths) {
        return null;
    }
    // Normalize Y position if it needs to jump to prev/next existing month
    var normalizedYPosition = yPosition;
    if (monthIndex !== newMonthIndex) {
        normalizedYPosition =
            newMonthIndex < monthIndex ? monthsData[newMonthIndex].weeks.length - 1 : firstWeekIndexInMonth;
    }
    return {
        monthIndex: newMonthIndex,
        weekIndex: normalizedYPosition,
        dayIndex: xPosition
    };
};
var moveCursor = function (focusedDayPosition, moveActionKey, monthsData, lastFocusedDay) {
    var moveOperation = moveMap[moveActionKey];
    var newFocusedPosition = moveCursorPosition(focusedDayPosition, moveOperation, monthsData);
    if (!newFocusedPosition) {
        return {
            focusedDay: lastFocusedDay,
            isLimitReached: true,
            pendingMoveKey: moveActionKey,
            prevFocusedDayOutOfBounds: false
        };
    }
    var newFocusedDay = monthsData[newFocusedPosition.monthIndex].weeks[newFocusedPosition.weekIndex][newFocusedPosition.dayIndex];
    var isCursorAnEmptyDay = typeof newFocusedDay === 'boolean' && !newFocusedDay;
    if (isCursorAnEmptyDay) {
        // Empty day, keep moving in the same direction until we find a day or it reaches a limit
        return moveCursor(newFocusedPosition, moveActionKey, monthsData, lastFocusedDay);
    }
    return {
        focusedDay: newFocusedDay,
        isLimitReached: false,
        pendingMoveKey: null,
        prevFocusedDayOutOfBounds: false
    };
};
var dayFocusReducer = function (state, action) {
    var type = action.type, _a = action.day, day = _a === void 0 ? null : _a, arrowKey = action.arrowKey, monthsData = action.monthsData;
    var focusedDay = state.focusedDay, pendingMoveKey = state.pendingMoveKey, isLimitReached = state.isLimitReached, prevFocusedDayOutOfBounds = state.prevFocusedDayOutOfBounds;
    switch (type) {
        case 'SET_FOCUS_DAY': {
            return __assign(__assign({}, state), { focusedDay: day });
        }
        case 'MOVE_FOCUS_DAY_WITH_ARROWS': {
            if (!day || !arrowKey || !monthsData) {
                return state;
            }
            var _b = moveCursor(day.position, arrowKey, monthsData, day), newFocusedDay = _b.focusedDay, newIsLimitReached = _b.isLimitReached, newPendingMoveKey = _b.pendingMoveKey;
            return __assign(__assign({}, state), { focusedDay: newFocusedDay, isLimitReached: newIsLimitReached, pendingMoveKey: newPendingMoveKey });
        }
        /**
         * There are 2 scenarios:
         * - Move cursor that is already rendered
         * - Move cursor that is not rendered in current view (prevFocusedDayOutOfBounds = true)
         */
        case 'MOVE_PENDING_CURSOR': {
            if (!isLimitReached || !pendingMoveKey || !focusedDay || !monthsData) {
                return state;
            }
            var position = prevFocusedDayOutOfBounds
                ? getNewFocusDayPosition(focusedDay, pendingMoveKey, monthsData)
                : focusedDay.position;
            if (prevFocusedDayOutOfBounds) {
                var monthIndex = position.monthIndex, weekIndex = position.weekIndex, dayIndex = position.dayIndex;
                var nextFocusedDay = monthsData[monthIndex].weeks[weekIndex][dayIndex];
                // If next cursor contains a valid day, just return it
                if (nextFocusedDay) {
                    return {
                        focusedDay: nextFocusedDay,
                        isLimitReached: false,
                        pendingMoveKey: null,
                        prevFocusedDayOutOfBounds: false
                    };
                }
            }
            var _c = moveCursor(position, pendingMoveKey, monthsData, focusedDay), newFocusedDay = _c.focusedDay, newLimitReached = _c.isLimitReached, newPendingOperation = _c.pendingMoveKey;
            return {
                focusedDay: newFocusedDay,
                isLimitReached: newLimitReached,
                pendingMoveKey: newPendingOperation,
                prevFocusedDayOutOfBounds: false
            };
        }
        /**
         * There are 2 scenarios:
         * - Recalculate focused position cursor that is already rendered
         * - Focused cursor is not rendered anymore, set prevFocusedDayOutOfBounds = true
         */
        case 'REPOSITION_CURRENT_CURSOR_IN_MONTH': {
            if (!focusedDay || !monthsData || !pendingMoveKey) {
                return state;
            }
            var newMonthIndex = monthsData.findIndex(function (monthData) { return monthData.month === focusedDay.month && monthData.year === focusedDay.year; });
            if (newMonthIndex === -1) {
                return __assign(__assign({}, state), { prevFocusedDayOutOfBounds: true });
            }
            var updatedCursor = __assign(__assign({}, focusedDay), { position: __assign(__assign({}, focusedDay.position), { monthIndex: newMonthIndex }) });
            return __assign(__assign({}, state), { focusedDay: updatedCursor });
        }
        default: {
            return state;
        }
    }
};
var useDayFocus = function (monthsData, onCalendarLimitReached) {
    var _a = __read(React.useReducer(dayFocusReducer, initialState), 2), _b = _a[0], focusedDay = _b.focusedDay, isLimitReached = _b.isLimitReached, pendingMoveKey = _b.pendingMoveKey, dispatch = _a[1];
    React.useEffect(function () {
        if (isLimitReached && pendingMoveKey && onCalendarLimitReached) {
            onCalendarLimitReached(pendingMoveKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLimitReached, pendingMoveKey]);
    React.useEffect(function () {
        dispatch({ type: 'REPOSITION_CURRENT_CURSOR_IN_MONTH', monthsData: monthsData });
        dispatch({ type: 'MOVE_PENDING_CURSOR', monthsData: monthsData });
    }, [monthsData]);
    var dayFocusActions = React.useMemo(function () { return ({
        clearFocusedDay: function () { return dispatch({ type: 'SET_FOCUS_DAY', day: null }); },
        moveFocusedDayWithArrows: function (arrowKey, day) {
            return dispatch({ type: 'MOVE_FOCUS_DAY_WITH_ARROWS', arrowKey: arrowKey, day: day, monthsData: monthsData });
        },
        setFocusedDay: function (day) { return dispatch({ type: 'SET_FOCUS_DAY', day: day }); }
    }); }, [monthsData]);
    return React.useMemo(function () { return ({
        focusedDay: focusedDay,
        dayFocusActions: dayFocusActions
    }); }, [focusedDay, dayFocusActions]);
};

export { useDayFocus };
